import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { AdditionalAnalysisParams } from '@ddt-web/domain';
import { AssetResolveResponse } from '@ddt-web/pas';
import { StoreService } from '@ddt-web/store-service';
import { WwePortfolioPosition } from '@ddt-web/wealth-web';
import { Observable, of } from 'rxjs';

import { PasPortfolioPositionsUtil } from '../../../utils/pas-portfolio-positions.util';
import { PasApiParamsService } from '../pas-api-params.service';
import { PasResponseProcessingService } from './pas-response-processing.service';

/**
 * Service that processes the PAS security-details API response for transient analysis workflows/ applications (IQ).
 * If this is the behavior needed, the override must be added in the app.module.ts:
 * { 
 *    provide: PasResponseProcessingService,
 *    useClass: PasTransientResponseProcessingService
 * } 
 * 
 * This supports:
 * - mapping the valid securities to a transient portfolio with the provided id
 * - error handling for security-details - if all securities fail for transient portfolio 
 *    - throwSecurityDetailsPartialPortfoliosErrors is not used because there is only one portfolio analysed (transient portfolio)
 * - filtering securities response
 * 	  - additionalAnalysisParams.securityResponseFilter needs to be provided
 */
@Injectable()
export class PasTransientResponseProcessingService extends PasResponseProcessingService {

	constructor(
		protected apiParamService: PasApiParamsService, // can move the method to the normal API service
		protected storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig
	) {
		super(apiParamService, storeService, storeConfig);
	}

	public getPortfoliosToLoad(
		ids: string[],
		positionDataFromApiCall?: AssetResolveResponse, // can be null when no valid securities sent
		throwSecurityDetailsPartialPortfoliosErrors?: boolean, // not used and not needed for transient analysis (only 1 transient portfolio analysed)
		additionalAnalysisParams?: AdditionalAnalysisParams // used for filtering securities response
	): Observable<WwePortfolioPosition[]> {
		let portfoliosToLoad = PasPortfolioPositionsUtil.getPortfoliosToLoadAsTransientAnalysis(ids[0], positionDataFromApiCall, additionalAnalysisParams);

		// check missing securities 
		const missingSecurities = Array.from(positionDataFromApiCall?.errorCodes?.keys() ?? []);

		if (missingSecurities.length && !portfoliosToLoad[0].children.length) { // all securities failed
			portfoliosToLoad = [];
		}

		return of(portfoliosToLoad);
	}
}
